import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { servicesAPI, bookingsAPI, authAPI } from '../utils/api';
import { toast } from 'react-toastify';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '../styles/Booking.css';

const formatDateKey = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0')
].join('-');

const Booking = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editBookingId = searchParams.get('edit');
  const { isAuthenticated } = useAuthStore();
  const [selectedService, setSelectedService] = useState(null);
  const [optionQuantities, setOptionQuantities] = useState({});
  const [selectedDate, setSelectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState({
    street: '',
    unit: '',
    city: '',
    province: '',
    postalCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [calendarAvailability, setCalendarAvailability] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const calendarRange = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setMonth(end.getMonth() + 3);
    return { start, end };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchInitialData = async () => {
      try {
        const [servicesResponse, profileResponse, bookingResponse] = await Promise.all([
          servicesAPI.getAll(),
          authAPI.getProfile(),
          editBookingId ? bookingsAPI.getById(editBookingId) : Promise.resolve(null)
        ]);
        setPhone(profileResponse.data.phone || '');
        setAddress({
          street: profileResponse.data.address?.street || '',
          unit: profileResponse.data.address?.unit || '',
          city: profileResponse.data.address?.city || '',
          province: profileResponse.data.address?.province || '',
          postalCode: profileResponse.data.address?.postalCode || ''
        });
        if (bookingResponse) {
          const booking = bookingResponse.data;
          if (!['pending', 'confirmed'].includes(booking.status)) {
            toast.error(t('booking.cannotEdit'));
            navigate('/my-bookings');
            return;
          }
          const bookingDate = new Date(booking.dateTime);
          const localDate = [
            bookingDate.getFullYear(),
            String(bookingDate.getMonth() + 1).padStart(2, '0'),
            String(bookingDate.getDate()).padStart(2, '0')
          ].join('-');
          const bookingService = servicesResponse.data.find(
            service => service._id === booking.serviceId?._id
          ) || booking.serviceId;
          setSelectedService(bookingService);
          setOptionQuantities(Object.fromEntries(
            (booking.serviceConfiguration || []).map((item) => [item.optionId, item.quantity])
          ));
          setSelectedDate(localDate);
          setPhone(booking.contactPhone || profileResponse.data.phone || '');
          setAddress({
            street: booking.cleaningAddress?.street || '',
            unit: booking.cleaningAddress?.unit || '',
            city: booking.cleaningAddress?.city || '',
            province: booking.cleaningAddress?.province || '',
            postalCode: booking.cleaningAddress?.postalCode || ''
          });
          setNotes(booking.clientNotes || '');
        } else {
          const requestedServiceId = searchParams.get('serviceId');
          if (requestedServiceId) {
          const requestedService = servicesResponse.data.find(
            (service) => service._id === requestedServiceId
          );
          if (requestedService) {
            setSelectedService(requestedService);
            try {
              const supplied = JSON.parse(searchParams.get('config') || '{}');
              setOptionQuantities(supplied);
            } catch {
              setOptionQuantities({});
            }
          }
          } else {
            navigate('/services', { replace: true });
        }
        }
      } catch (err) {
        console.error('Error fetching services:', err);
      }
    };

    fetchInitialData();
  }, [editBookingId, isAuthenticated, navigate, searchParams, t]);

  useEffect(() => {
    if (!selectedService) {
      setCalendarAvailability({});
      return;
    }

    const fetchCalendarAvailability = async () => {
      try {
        setCalendarLoading(true);
        const response = await bookingsAPI.getAvailabilityCalendar(
          selectedService._id,
          formatDateKey(calendarRange.start),
          formatDateKey(calendarRange.end),
          editBookingId,
          optionQuantities
        );
        setCalendarAvailability(response.data.days || {});
      } catch (err) {
        setCalendarAvailability({});
        toast.error(t('booking.availabilityError'));
      } finally {
        setCalendarLoading(false);
      }
    };

    fetchCalendarAvailability();
  }, [calendarRange, editBookingId, optionQuantities, selectedService, t]);

  const configuredTotals = useMemo(() => {
    if (!selectedService) return { price: 0, minutes: 0, items: [] };
    let price = Number(selectedService.basePrice);
    let minutes = Math.round(Number(selectedService.duration) * 60);
    const items = (selectedService.configurationItems || []).map((item) => {
      const quantity = optionQuantities[item._id] ?? item.defaultQuantity ?? 0;
      const additional = Math.max(0, quantity - (item.defaultQuantity || 0));
      price += additional * Number(item.pricePerAdditional || 0);
      minutes += additional * Number(item.minutesPerAdditional || 0);
      return { ...item, quantity };
    });
    return { price, minutes, items };
  }, [optionQuantities, selectedService]);

  useEffect(() => {
    if (!selectedService || !selectedDate) {
      setQuote(null);
      return;
    }
    let active = true;
    bookingsAPI.getQuote(
      selectedService._id,
      `${selectedDate}T12:00`,
      editBookingId,
      optionQuantities
    ).then((response) => {
      if (active) setQuote(response.data);
    }).catch(() => {
      if (active) setQuote(null);
    });
    return () => { active = false; };
  }, [editBookingId, optionQuantities, selectedDate, selectedService]);

  const handleBooking = async (e) => {
    e.preventDefault();

    if (
      !selectedService ||
      !selectedDate ||
      !phone.trim() ||
      !address.street ||
      !address.unit ||
      !address.city ||
      !address.province ||
      !address.postalCode
    ) {
      toast.error(t('booking.requiredFields'));
      return;
    }

    try {
      setLoading(true);
      const bookingData = {
        serviceId: selectedService._id,
        date: selectedDate,
        contactPhone: phone,
        cleaningAddress: address,
        clientNotes: notes,
        optionQuantities
      };

      if (editBookingId) {
        await bookingsAPI.update(editBookingId, bookingData);
        toast.success(t('booking.updateSuccess'));
      } else {
        await bookingsAPI.create(bookingData);
        toast.success(t('booking.bookingSuccess'));
      }
      
      // Reset form
      setSelectedService(null);
      setSelectedDate('');
      setNotes('');

      // Redirect to bookings page
      setTimeout(() => navigate('/my-bookings'), 2000);
    } catch (err) {
      toast.error(err.response?.data?.message || t('booking.createError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="booking-page">
      <h1>{editBookingId ? t('booking.editTitle') : t('booking.title')}</h1>

      <form className="booking-form" onSubmit={handleBooking}>
        <div className="form-group date-picker-group">
          <div className="schedule-heading">
            <label>{t('booking.selectDate')}</label>
            <div className="schedule-legend">
              <span><i className="legend-available" />{t('booking.available')}</span>
              <span><i className="legend-unavailable" />{t('booking.unavailable')}</span>
            </div>
          </div>
          {!selectedService ? (
            <p className="schedule-message">{t('booking.chooseServiceFirst')}</p>
          ) : calendarLoading ? (
            <p className="schedule-message">{t('common.loading')}</p>
          ) : (
            <div className="customer-calendar-wrap">
              <Calendar
                value={selectedDate ? new Date(`${selectedDate}T12:00:00`) : null}
                onChange={(date) => {
                  setSelectedDate(formatDateKey(date));
                }}
                locale={i18n.resolvedLanguage?.startsWith('fr') ? 'fr-CA' : 'en-CA'}
                minDate={calendarRange.start}
                maxDate={calendarRange.end}
                minDetail="month"
                tileDisabled={({ date, view }) =>
                  view === 'month' && calendarAvailability[formatDateKey(date)] !== true
                }
                tileClassName={({ date, view }) => {
                  if (view !== 'month') return null;
                  return calendarAvailability[formatDateKey(date)]
                    ? 'customer-date-available'
                    : 'customer-date-unavailable';
                }}
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="phone">{t('profile.phone')}</label>
          <input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
            required
          />
        </div>

        <fieldset className="address-fields">
          <legend>{t('booking.cleaningAddress')}</legend>
          <div className="form-group">
            <label htmlFor="street">{t('booking.street')}</label>
            <input
              id="street"
              type="text"
              value={address.street}
              onChange={(e) => setAddress({ ...address, street: e.target.value })}
              autoComplete="street-address"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="unit">{t('booking.unitRequired')}</label>
            <input
              id="unit"
              type="text"
              value={address.unit}
              onChange={(e) => setAddress({ ...address, unit: e.target.value })}
              autoComplete="address-line2"
              required
            />
          </div>
          <div className="address-row">
            <div className="form-group">
              <label htmlFor="city">{t('booking.city')}</label>
              <input
                id="city"
                type="text"
                value={address.city}
                onChange={(e) => setAddress({ ...address, city: e.target.value })}
                autoComplete="address-level2"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="province">{t('booking.province')}</label>
              <input
                id="province"
                type="text"
                value={address.province}
                onChange={(e) => setAddress({ ...address, province: e.target.value })}
                autoComplete="address-level1"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="postalCode">{t('booking.postalCode')}</label>
              <input
                id="postalCode"
                type="text"
                value={address.postalCode}
                onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
                autoComplete="postal-code"
                required
              />
            </div>
          </div>
        </fieldset>

        <div className="form-group">
          <label>{t('booking.notes')}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('booking.notesPlaceholder')}
            rows="4"
          />
        </div>

        {selectedService && (
          <div className="booking-summary">
            <h3>{t('booking.cleaningSummary')}</h3>
            {selectedService.configuratorEnabled && configuredTotals.items.filter((item) => Number(item.quantity) > 0).map((item) => (
              <p key={item._id}>{item.name?.[i18n.resolvedLanguage?.startsWith('fr') ? 'fr' : 'en']}: <strong>{item.quantity}</strong></p>
            ))}
            {quote?.discountPercentage > 0 && (
              <div className="booking-discount">
                <strong>{t('booking.discountApplied', { percentage: quote.discountPercentage })}</strong>
                <span>{quote.discountReason?.[i18n.resolvedLanguage?.startsWith('fr') ? 'fr' : 'en']}</span>
              </div>
            )}
            <h3>{t('booking.totalPrice')}</h3>
            <p className="price">
              {quote?.discountPercentage > 0 && <del>${quote.originalPrice}</del>}
              ${quote?.totalPrice ?? configuredTotals.price}
            </p>
          </div>
        )}

        <button type="submit" className="submit-btn" disabled={loading}>
          {loading
            ? t('common.loading')
            : editBookingId
              ? t('booking.updateBooking')
              : t('booking.book')}
        </button>
      </form>
    </div>
  );
};

export default Booking;
